import React, { useMemo, useState } from 'react';
import { Input, Card, Alert } from 'antd';
import { TFilterGroup } from '@/types/TSmartCRM';
import { useSmartVariables } from 'src/Hooks/SmartCRM/useSmartVariables';
import { EnrollmentFilterBuilder } from './Workflows/EnrollmentFilterBuilder';
import { useOutbound } from 'src/Hooks/SmartCRM/useOutbound';
import { convertBackendToFilterGroups } from '@/utils/SmartCRM/filterUtils';

const { TextArea } = Input;

const CampaignDetailsStep: React.FC = () => {
	const { fields } = useSmartVariables(false);
	const { currentCampaign, updateCurrentCampaign } = useOutbound();
	const [errors, setErrors] = useState<{ name?: string; goal?: string }>({});

	const enrollmentRules = useMemo(() => {
		if (!currentCampaign?.enrollmentRules || fields.length === 0) {
			return [];
		}

		const firstRule = currentCampaign.enrollmentRules[0];
		if (firstRule && typeof firstRule === 'object' && !firstRule.id) {
			const mockFilterView = {
				filterGroups: {
					group_1: {
						name: 'Enrollment Rules',
						filters: currentCampaign.enrollmentRules as any,
					},
				},
			};

			return convertBackendToFilterGroups(mockFilterView.filterGroups, fields);
		}

		return currentCampaign.enrollmentRules;
	}, [currentCampaign?.enrollmentRules, fields]);

	const validateField = (fieldName: 'name' | 'goal', value: string) => {
		const newErrors = { ...errors };

		if (!value?.trim()) {
			newErrors[fieldName] =
				fieldName === 'name' ? 'Task title is required' : 'Task goal is required';
		} else {
			delete newErrors[fieldName];
		}

		setErrors(newErrors);
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		updateCurrentCampaign({ name: value });
		validateField('name', value);
	};

	const handleGoalChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = e.target.value;
		updateCurrentCampaign({ goal: value });
		validateField('goal', value);
	};

	const handleEnrollmentRulesChange = (rules: TFilterGroup[]) => {
		updateCurrentCampaign({ enrollmentRules: rules });
	};

	return (
		<div className="w-full max-w-4xl mx-auto py-8 px-8">
			<div className="space-y-8">
				<div>
					<h2 className="text-xl font-semibold mb-6 text-center text-gray-800">
						Outbound Task Details
					</h2>
				</div>

				<Card title="Task Information" className="shadow-sm">
					<div className="space-y-6">
						<div>
							<label className="block text-sm font-medium mb-3 text-gray-700">
								Outbound Task Title <span className="text-red-500">*</span>
							</label>
							<Input
								placeholder="e.g., Q1 Lead Generation Task"
								value={currentCampaign?.name || ''}
								onChange={handleNameChange}
								className="w-full"
								size="large"
								status={errors.name ? 'error' : ''}
							/>
							{errors.name && <div className="text-red-500 text-sm mt-1">{errors.name}</div>}
						</div>

						<div>
							<label className="block text-sm font-medium mb-3 text-gray-700">
								Task Goal <span className="text-red-500">*</span>
							</label>
							<TextArea
								placeholder="Describe the main objective of this outbound task... e.g., Generate qualified leads for our Q1 sales pipeline by reaching out to prospects in the technology sector."
								value={currentCampaign?.goal || ''}
								onChange={handleGoalChange}
								rows={4}
								className="w-full"
								size="large"
								status={errors.goal ? 'error' : ''}
							/>
							{errors.goal && <div className="text-red-500 text-sm mt-1">{errors.goal}</div>}
						</div>
					</div>
				</Card>

				<Card title="Enrollment Rules" className="shadow-sm">
					<div className="space-y-6">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-4">
								Define customer enrollment criteria for this task
							</label>
							<div className="text-sm text-gray-500 mb-6">
								Set up rules to determine which customers will be enrolled in this outbound task
							</div>
						</div>

						{fields.length === 0 && (
							<Alert
								message="No Smart Variables Available"
								description="You need to create smart variables in customer info fields section first before adding enrollment rules."
								type="warning"
								showIcon
								className="mb-6"
							/>
						)}

						<EnrollmentFilterBuilder
							filterGroups={enrollmentRules}
							onChange={handleEnrollmentRulesChange}
							fields={fields}
						/>
					</div>
				</Card>
			</div>
		</div>
	);
};

export default CampaignDetailsStep;
